import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AddProductComponent } from './add-product/add-product.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NavBarComponent } from './nav-bar/nav-bar.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PaginatorComponent } from './paginator/paginator.component';
import { ProductListComponent } from './product-list/product-list.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatTooltipModule, MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { DeleteConfirmationDialogComponent } from './delete-confirmation-dialog/delete-confirmation-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { AddOrderComponent } from './add-order/add-order.component';
import { IsNoPaidOrderPopUpComponent } from './is-no-paid-order-pop-up/is-no-paid-order-pop-up.component';
import { SelectClientPopUpComponent } from './select-client-pop-up/select-client-pop-up.component';
import { AddClientComponent } from './add-client/add-client.component';
import { OrderListComponent } from './order-list/order-list.component';
import { DatePipe } from '@angular/common';
import { NumberSuffixPipe } from './utils/number-suffix.pipe';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { QRCodeComponent, QRCodeModule } from 'angularx-qrcode';
import { InsufficientQuantityDialogComponent } from './insufficient-quantity-dialog/insufficient-quantity-dialog.component';
import { GenericErrorDialogComponent } from './generic-error-dialog/generic-error-dialog.component';
import { InvalidSellingPriceDialogComponent } from './invalid-selling-price-dialog/invalid-selling-price-dialog.component';
import { AddCashLoanComponent } from './add-cash-loan/add-cash-loan.component';
import { LoanListComponent } from './loan-list/loan-list.component';
import { ClientDetailsComponent } from './client-details/client-details.component';
import { ClientOrderListComponent } from './client-order-list/client-order-list.component';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { LoaderComponent } from './loader/loader.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoginComponent } from './login/login.component';
import { SignupComponent } from './signup/signup.component';
import { EmailVerificationComponent } from './email-verification/email-verification.component';
import { AdminApprovalsComponent } from './admin-approvals/admin-approvals.component';
import { CashDiscrepancyComponent } from './cash-discrepancy/cash-discrepancy.component';
import { StockDiscrepancyComponent } from './stock-discrepancy/stock-discrepancy.component';
import { SharedModule } from './shared/shared.module';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { FinancialDashboardComponent } from './financial-dashboard/financial-dashboard.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { NgChartsModule } from 'ng2-charts';
import { PopupComponent } from './shared/popup/popup.component';
import { DiscrepancyDashboardComponent } from './discrepancy-dashboard/discrepancy-dashboard.component';
import { AdminCategoriesComponent } from './admin-categories/admin-categories.component';
import { CashHistoryComponent } from './cash-history/cash-history.component';
import { CashReconciliationComponent } from './cash-reconciliation/cash-reconciliation.component';
import { CashAmountDialogComponent } from './shared/cash-amount-dialog/cash-amount-dialog.component';
import { F8LinkConfirmDialogComponent } from './shared/f8-link-confirm-dialog/f8-link-confirm-dialog.component';
import { ReturnOrderComponent } from './return-order/return-order.component';
import { SmartRefundFinderComponent } from './smart-refund-finder/smart-refund-finder.component';
import { AvoirListComponent } from './avoir-list/avoir-list.component';
import { AvoirDetailComponent } from './avoir-detail/avoir-detail.component';
import { ReconciliationWorkbenchComponent } from './reconciliation-workbench/reconciliation-workbench.component';
import { PrintBarcodeDialogComponent } from './print-barcode-dialog/print-barcode-dialog.component';

registerLocaleData(localeFr, 'fr'); // Register French locale

/** Custom options for material tooltips */
export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 300,
  hideDelay: 100,
  touchendHideDelay: 100,
  position: 'above',
  touchGestures: 'auto',
  disableTooltipInteractivity: false,
};

@NgModule({
  declarations: [
    AppComponent,
    AddProductComponent,
    NavBarComponent,
    PaginatorComponent,
    ProductListComponent,
    // DeleteConfirmationDialogComponent, // Only declared in SharedModule
    AddOrderComponent,
    IsNoPaidOrderPopUpComponent,
    SelectClientPopUpComponent,
    AddClientComponent,
    OrderListComponent,
    NumberSuffixPipe,
    OrderDetailsComponent,
    InsufficientQuantityDialogComponent,
    GenericErrorDialogComponent,
    InvalidSellingPriceDialogComponent,
    AddCashLoanComponent,
    LoanListComponent,
    ClientDetailsComponent,
    ClientOrderListComponent,
    LoaderComponent,
    LoginComponent,
    SignupComponent,
    EmailVerificationComponent,
    AdminApprovalsComponent,
    CashDiscrepancyComponent,
    StockDiscrepancyComponent,
    FinancialDashboardComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    PopupComponent,
    DiscrepancyDashboardComponent,
    AdminCategoriesComponent,
    PopupComponent,
    DiscrepancyDashboardComponent,
    AdminCategoriesComponent,
    CashHistoryComponent,
    CashReconciliationComponent,
    CashAmountDialogComponent,
    F8LinkConfirmDialogComponent,
    ReturnOrderComponent,
    SmartRefundFinderComponent,
    AvoirListComponent,
    AvoirDetailComponent,
    ReconciliationWorkbenchComponent,
    PrintBarcodeDialogComponent
  ],

  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    QRCodeModule,
    SharedModule,
    NgChartsModule,
  ],
  providers: [
    DatePipe,
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
